import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Formik, Form, Field } from "formik";
import {
  Button,
  Paper,
  Select,
  TextField,
  Typography,
  Divider,
  Switch,
  FormGroup,
  FormControlLabel,
} from "@material-ui/core";
import { FormControl, MenuItem } from "@mui/material";

import { createRecordHandHygiene } from "../../../service/endpoint-manager";
import { validationSchema } from "../../../utils/hhValidationSchema";
import clsx from "clsx";
import styles from "../../../styles.module.css";

// QuantumHub Library
import { Timer } from "@thequantumhub/timer";
// import Timer from "../Shared/Timer";

import { MAX_DROPDOWN_HEIGHT } from "../../../types/micellinous-constants";

import { renderCommaSeparatedValues } from "@thequantumhub/iam-package";
// import { renderCommaSeparatedValues } from "../../../utils/helperFunctions";

import { useSnackbar } from "notistack";

const useStyles = makeStyles((theme) => ({}));

const HHForm = ({ nurse, facility, unit }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [timerValues, setTimerValues] = useState([0]);

  const [isWashToggled, setIsWashToggled] = useState(false);
  const [isRubToggled, setIsRubToggled] = useState(false);
  const [isMissedToggled, setIsMissedToggled] = useState(false);

  const [resetKey, setResetKey] = useState(0);

  const getInitialValues = () => ({
    facility: facility,
    unit: unit,
    nurse: nurse,
    formQuestions: {
      healthCareWorker: "",
      handHygieneMoment: "",
      outcome: "",
      custom: {
        rings: false,
        gloves: false,
        nails: false,
        bracelet: false,
      },
      comment: "",
      timer: 0,
    },
    isTimerRunning: false,
  });

  const [initialValues, setInitialValues] = useState(getInitialValues());

  useEffect(() => {
    setInitialValues(getInitialValues());
  }, [facility, unit, nurse]);

  const handleTimerStop = (finalValue, index) => {
    setTimerValues((prevTimerValues) => {
      const updatedValues = [...prevTimerValues];
      updatedValues[index] = finalValue;
      return updatedValues;
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      validateOnChange={true}
      validateOnBlur={true}
      onSubmit={(values, { resetForm, setSubmitting, setFieldValue }) => {
        const updatedValues = (({ isTimerRunning, ...rest }) => rest)(values);

        createRecordHandHygiene(updatedValues)
          .then((response) => {
            enqueueSnackbar(`${response?.data?.message}`, {
              variant: "success",
            });
          })
          .catch((error) => {
            enqueueSnackbar(error, {
              variant: "error",
            });
            console.error("API Error:", error);
          })
          .finally(() => {
            setSubmitting(false);
            setIsWashToggled(false);
            setIsRubToggled(false);
            setIsMissedToggled(false);

            resetForm();
            setResetKey((prevKey) => prevKey + 1);
          });
      }}
      key={resetKey}
    >
      {({
        values,
        handleChange,
        setFieldValue,
        isSubmitting,
        resetForm,
        errors,
        validateForm,
      }) => {
        const handleReset = () => {
          resetForm();
          setIsWashToggled(false);
          setIsRubToggled(false);
          setIsMissedToggled(false);
          setResetKey((prev) => prev + 1);
        };

        const handleToggleChange = (outcome) => {
          const isWash = outcome === "wash";
          const isRub = outcome === "rub";
          const isMissed = outcome === "missed";

          setIsWashToggled(isWash);
          setIsRubToggled(isRub);
          setIsMissedToggled(isMissed);

          setFieldValue("formQuestions.outcome", outcome);

          if (isWash || isRub) {
            setTimerValues([0]);
            setFieldValue("formQuestions.timer", 0);
          }
        };

        const handleRingsToggle = () => {
          const newValue = !values.formQuestions.custom.rings;
          setFieldValue("formQuestions.custom.rings", newValue);
        };

        const handleGlovesToggle = () => {
          const newValue = !values.formQuestions.custom.gloves;
          setFieldValue("formQuestions.custom.gloves", newValue);
        };

        const handleNailsToggle = () => {
          const newValue = !values.formQuestions.custom.nails;
          setFieldValue("formQuestions.custom.nails", newValue);
        };

        const handleBraceletToggle = () => {
          const newValue = !values.formQuestions.custom.bracelet;
          setFieldValue("formQuestions.custom.bracelet", newValue);
        };

        const commaSeparatedValues = renderCommaSeparatedValues(errors).slice(
          0,
          -2
        ); // Remove the trailing comma and space

        return (
          <Form className={classes.form}>
            <Paper className={styles.formPaper}>
              <div className={styles.handHygieneFormSection}>
                <FormControl variant="outlined" fullWidth margin="normal">
                  <div className={styles.titleFlex}>
                    <Typography
                      variant="body"
                      className={styles.handHygieneTitleBold}
                    >
                      HealthCare
                    </Typography>
                    <div />
                    <Typography
                      variant="body"
                      className={styles.handHygieneTitleSimple}
                    >
                      &nbsp;Worker
                    </Typography>
                  </div>
                  <Field
                    as={Select}
                    name="formQuestions.healthCareWorker"
                    variant="outlined"
                    MenuProps={{
                      style: {
                        maxHeight: MAX_DROPDOWN_HEIGHT,
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="Physician">Physician</MenuItem>
                    <MenuItem value="Registered Staff">
                      Registered Staff
                    </MenuItem>
                    <MenuItem value="PSW">PSW</MenuItem>
                    <MenuItem value="Student">Student</MenuItem>
                    <MenuItem value="Dietary">Dietary</MenuItem>
                    <MenuItem value="Social worker">Social worker</MenuItem>
                    <MenuItem value="Pastoral care">Pastoral care</MenuItem>
                    <MenuItem value="Lab/X-ray/blood collection">
                      Lab/X-ray/blood collection
                    </MenuItem>
                    <MenuItem value="OT/PT/PTA (Therapy Staff)">
                      OT/PT/PTA (Therapy Staff)
                    </MenuItem>
                    <MenuItem value="Housekeeping">Housekeeping</MenuItem>
                    <MenuItem value="Laundry staff">Laundry staff</MenuItem>
                    <MenuItem value="Volunteer">Volunteer</MenuItem>
                    <MenuItem value="Programs">Programs</MenuItem>
                    <MenuItem value="Essential Caregiver">
                      Essential Caregiver
                    </MenuItem>
                    <MenuItem value="Private caregiver">
                      Private caregiver
                    </MenuItem>
                    <MenuItem value="One-to-one worker">
                      One-to-one worker
                    </MenuItem>
                    <MenuItem value="Dental services">Dental services</MenuItem>
                    <MenuItem value="BSO">BSO</MenuItem>
                    <MenuItem value="Optometry">Optometry</MenuItem>
                    <MenuItem value="Hair Stylist">Hair Stylist</MenuItem>
                    <MenuItem value="Nurse Practitioner">
                      Nurse Practitioner
                    </MenuItem>
                    <MenuItem value="General visitor">General visitor</MenuItem>
                    <MenuItem value="Footcare nurse">Footcare nurse</MenuItem>
                    <MenuItem value="Admin">Admin</MenuItem>
                    <MenuItem value="Security">Security</MenuItem>
                    <MenuItem value="Maintenance">Maintenance</MenuItem>
                  </Field>
                </FormControl>
              </div>
              <Divider />
              <div className={styles.handHygieneFormSection}>
                <FormControl variant="outlined" fullWidth margin="normal">
                  <div className={styles.titleFlex}>
                    <Typography
                      variant="body"
                      className={styles.handHygieneTitleBold}
                    >
                      Hand Hygiene
                    </Typography>
                    <div style={{ margin: "4px" }} />
                    <Typography
                      variant="body"
                      className={styles.handHygieneTitleSimple}
                    >
                      Moment
                    </Typography>
                  </div>
                  <Field
                    as={Select}
                    name="formQuestions.handHygieneMoment"
                    variant="outlined"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="BEF-PAT/ENV">1. BEF-PAT/ENV</MenuItem>
                    <MenuItem value="BEF-ASP">2. BEF-ASP</MenuItem>
                    <MenuItem value="AFT-BFL">3. AFT-BFL</MenuItem>
                    <MenuItem value="AFT-PAT/ENV">4. AFT-PAT/ENV</MenuItem>
                    <MenuItem value="BEF Nurse Med Pass">
                      5. BEF Nurse Med Pass
                    </MenuItem>
                    <MenuItem value="AFT Nurse Med Pass">
                      6. AFT Nurse Med Pass
                    </MenuItem>
                    <MenuItem value="BEF Resident Meal-time">
                      7. BEF Resident Meal-time
                    </MenuItem>
                    <MenuItem value="Resident Nourishment Pass">
                      8. Resident Nourishment Pass
                    </MenuItem>
                    <MenuItem value="General">9. General</MenuItem>
                  </Field>
                </FormControl>
              </div>
              <Divider />
              <div className={styles.handHygieneFormSection}>
                <FormControl
                  component="fieldset"
                  className={classes.formControl}
                >
                  <div className={styles.titleFlex}>
                    <Typography
                      variant="body"
                      className={styles.handHygieneTitleBold}
                    >
                      Timed
                    </Typography>
                    <div style={{ margin: "4px" }} />
                    <Typography
                      variant="body"
                      className={styles.handHygieneTitleSimple}
                    >
                      Outcome
                    </Typography>
                  </div>
                  <FormGroup>
                    <div className={styles.timerContainer}>
                      <p className={styles.outcomeLabel}>Wash</p>
                      <FormControlLabel
                        labelPlacement="start"
                        className={styles.outcomeLabel}
                        control={
                          <Switch
                            checked={isWashToggled}
                            onChange={(event) => {
                              if (event.target.checked) {
                                handleToggleChange("wash");
                              } else {
                                handleToggleChange("");
                              }
                            }}
                            name="formQuestions.washSwitch"
                          />
                        }
                      />
                      {isWashToggled && (
                        <>
                          {/* {test} */}
                          <Timer
                            timer={timerValues[0]}
                            onTimerChange={(finalValue) =>
                              handleTimerStop(finalValue, 0)
                            }
                            setFieldValue={setFieldValue}
                            name="formQuestions.timer"
                            isRunningName="isTimerRunning"
                          />
                        </>
                      )}
                    </div>
                    <div className={styles.timerContainer}>
                      <p className={styles.outcomeLabel}>Rub</p>
                      <FormControlLabel
                        labelPlacement="start"
                        className={styles.outcomeLabel}
                        control={
                          <Switch
                            checked={isRubToggled}
                            onChange={(event) => {
                              if (event.target.checked) {
                                handleToggleChange("rub");
                              } else {
                                handleToggleChange("");
                              }
                            }}
                            name="formQuestions.rubSwitch"
                          />
                        }
                      />
                      {isRubToggled && (
                        <Timer
                          timer={timerValues[0]}
                          onTimerChange={(finalValue) =>
                            handleTimerStop(finalValue, 0)
                          }
                          setFieldValue={setFieldValue}
                          name="formQuestions.timer"
                          isRunningName="isTimerRunning"
                        />
                      )}
                    </div>
                    <div className={styles.timerContainer}>
                      <p className={styles.outcomeLabel}>Missed</p>
                      <FormControlLabel
                        labelPlacement="start"
                        className={styles.outcomeLabel}
                        control={
                          <Switch
                            checked={isMissedToggled}
                            onChange={(event) => {
                              if (event.target.checked) {
                                handleToggleChange("missed");
                              } else {
                                handleToggleChange("");
                              }
                            }}
                            name="formQuestions.missedSwitch"
                          />
                        }
                      />
                    </div>
                  </FormGroup>
                </FormControl>
              </div>
              <Divider />
              <div className={styles.handHygieneFormSection}>
                <FormControl
                  component="fieldset"
                  className={classes.formControl}
                >
                  <div className={styles.titleFlex}>
                    <Typography
                      variant="body"
                      className={styles.handHygieneTitleBold}
                    >
                      Custom
                    </Typography>
                    <div style={{ margin: "4px" }} />
                    <Typography
                      variant="body"
                      className={styles.handHygieneTitleSimple}
                    >
                      &nbsp;Item
                    </Typography>
                  </div>
                  <FormGroup>
                    <div className={styles.timerContainer}>
                      <p className={styles.outcomeLabel}>Rings</p>
                      <FormControlLabel
                        labelPlacement="start"
                        control={
                          <Switch
                            checked={values.formQuestions.custom.rings}
                            onChange={handleRingsToggle}
                          />
                        }
                        className={styles.outcomeLabel}
                      />
                    </div>
                    <div className={styles.timerContainer}>
                      <p className={styles.outcomeLabel}>Gloves</p>
                      <FormControlLabel
                        labelPlacement="start"
                        control={
                          <Switch
                            checked={values.formQuestions.custom.gloves}
                            onChange={handleGlovesToggle}
                          />
                        }
                        className={styles.outcomeLabel}
                      />
                    </div>
                    <div className={styles.timerContainer}>
                      <p className={styles.outcomeLabel}>Nails</p>
                      <FormControlLabel
                        labelPlacement="start"
                        control={
                          <Switch
                            checked={values.formQuestions.custom.nails}
                            onChange={handleNailsToggle}
                          />
                        }
                        className={styles.outcomeLabel}
                      />
                    </div>
                    <div className={styles.timerContainer}>
                      <p className={styles.outcomeLabel}>Bracelet</p>
                      <FormControlLabel
                        labelPlacement="start"
                        control={
                          <Switch
                            checked={values.formQuestions.custom.bracelet}
                            onChange={handleBraceletToggle}
                          />
                        }
                        className={styles.outcomeLabel}
                      />
                    </div>
                  </FormGroup>
                </FormControl>
              </div>
              <Divider />
              <div className={styles.handHygieneFormSection}>
                <div className={styles.titleFlex}>
                  <Typography
                    variant="body"
                    className={styles.handHygieneTitleBold}
                  >
                    Comment
                  </Typography>
                </div>
                <TextField
                  name="formQuestions.comment"
                  value={values.formQuestions.comment}
                  onChange={handleChange}
                  multiline
                  rows={4}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                />
              </div>
            </Paper>
            <Typography variant="body1" className={styles.errorTextColor}>
              {commaSeparatedValues}
            </Typography>
            <div className={styles.flexCenter}>
              <Button
                className={clsx(`${styles.redButton} ${styles.buttonMargin}`)}
                onClick={() => handleReset(resetForm, setFieldValue)}
              >
                Reset
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={styles.primaryButton}
                disabled={isSubmitting}
              >
                Submit
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default HHForm;
